import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { FormGroup, Switch, FormControlLabel, Checkbox } from '@mui/material'
import { AnySwitchProps } from './AnySwitch.types'
import { AnyComponentRef } from './AnyComponent.types'

function AnySwitch({
    name,
    label,
    initialValue = false,
    disabled,
    placement = 'end',
    color = 'primary',
    ui = 'switch',
    register,
    notifier,
}: AnySwitchProps) {
    const [value, setValue] = useState<boolean>(initialValue)
    const [dirty, setDirty] = useState(false)

    const ref = useRef<AnyComponentRef<boolean>>({
        valid: true,
        dirty,
        value,
        setBlur: () => {},
    })

    const handleChange = (event: React.ChangeEvent<{}>, checked: boolean) => {
        setDirty(true)
        setValue(checked)
    }

    const onNotify = useCallback(
        (value: boolean) => {
            if (notifier) {
                notifier(name, value)
            }
        },
        [notifier, name]
    )

    useEffect(() => {
        ref.current.value = value
        ref.current.dirty = dirty

        onNotify(value)
    }, [value, dirty, onNotify])

    useEffect(() => {
        register(name, ref.current)
    }, [register, name, ref])

    useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    return (
        <FormGroup row>
            <FormControlLabel
                label={label}
                labelPlacement={placement}
                control={
                    <>
                        {ui === 'switch' && (
                            <Switch
                                checked={value}
                                onChange={handleChange}
                                value={name}
                                color={color}
                                disabled={disabled}
                            />
                        )}
                        {ui === 'checkbox' && (
                            <Checkbox
                                checked={value}
                                onChange={handleChange}
                                value={name}
                                color={color}
                                disabled={disabled}
                            />
                        )}
                    </>
                }
            />
        </FormGroup>
    )
}

export default memo(AnySwitch)
